import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ContentIndex } from '@ezishizi/content-schema';
import { CHALLENGE_LESSON_ID, type MasteryState } from '@ezishizi/learning-core';
import { challengeCandidates } from '../lib/dailyChallenge.ts';
import { loadIndex } from '../lib/content.ts';
import { getAdventureMode } from '../lib/adventure.ts';
import { buildPath, currentNode, pathStatuses, unlockedOrder, type PathNode } from '../lib/path.ts';
import { buildFilms, filmUnlocked, filmWatched } from '../lib/films.ts';
import { toProgressMap, type ProgressMap } from '../lib/progress.ts';
import {
  challengesOn,
  challengeStreakOn,
  emptyChallengeStats,
  store,
  streakOn,
  todayKey,
  type ChallengeStats,
  type DailyStat,
  type Streak,
} from '../lib/store/index.ts';
import { useSession } from '../state/session.ts';
import { Shizi } from '../components/mascot/Shizi.tsx';
import { Avatar, normalizeAvatar } from '../components/avatars.tsx';
import { FlameIcon, ModuleIcon, StarIcon } from '../components/icons.tsx';
import { ClapperIcon, StickerBookIcon } from '../components/rewardIcons.tsx';
import { Screen } from '../components/ui.tsx';
import { useApp } from '../state/app.ts';
import { useProfile } from './useProfile.ts';

/** Auth-looking failures (dead session, expired/invalid token) should sign the user out
 * rather than silently rendering zeros; anything else is a transient load failure. */
function isAuthError(err: unknown): boolean {
  const status = (err as { status?: number; code?: number } | null)?.status;
  const code = (err as { status?: number; code?: number } | null)?.code;
  if (status === 401 || status === 403 || code === 401 || code === 403) return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /jwt|token|refresh|not signed in|unauthori[sz]ed/i.test(msg);
}

const NODE_LABEL: Record<PathNode['kind'], string> = {
  learn: 'New words',
  listen: 'Listen',
  speak: 'Speak',
  play: 'Play',
  read: 'Read',
  write: 'Write',
  review: 'Review',
  pinyin: 'Pinyin',
  game: 'Game',
  word: 'Word',
  challenge: 'Challenge',
  practice: 'Reading practice',
  reward: 'Reward',
};

export function Home() {
  const adventureMode = getAdventureMode();
  const nav = useNavigate();
  const profile = useProfile();
  const setMistakeChars = useSession((s) => s.setMistakeChars);
  const [index, setIndex] = useState<ContentIndex | null>(null);
  const [today, setToday] = useState<DailyStat | null>(null);
  const [streak, setStreak] = useState<Streak | null>(null);
  const [progress, setProgress] = useState<ProgressMap>(new Map());
  const [weak, setWeak] = useState<string[]>([]);
  const [mastery, setMastery] = useState<MasteryState[]>([]);
  const [challengeStats, setChallengeStats] = useState<ChallengeStats>(emptyChallengeStats());
  const [error, setError] = useState(false);

  const loadProgress = useCallback(async () => {
    if (!profile) return;
    setError(false);
    const [dailyRes, streakRes, progressRes, masteryRes, challengeRes] = await Promise.allSettled([
      store.getDailyStats(profile.id, 1),
      store.getStreak(profile.id),
      store.getProgress(profile.id),
      store.getMastery(profile.id),
      store.getChallengeStats(profile.id),
    ]);
    // A failed challenge-stats fetch isn't an auth-worthy error; it just falls back to zeros.
    const rejected = [dailyRes, streakRes, progressRes, masteryRes].filter(
      (r): r is PromiseRejectedResult => r.status === 'rejected',
    );
    if (rejected.some((r) => isAuthError(r.reason))) {
      await store.signOut();
      useApp.getState().setProfiles(null);
      void nav({ to: '/auth' });
      return;
    }
    if (dailyRes.status === 'fulfilled') {
      setToday(dailyRes.value.find((x) => x.day === todayKey()) ?? null);
    }
    if (streakRes.status === 'fulfilled') setStreak(streakRes.value);
    if (progressRes.status === 'fulfilled') setProgress(toProgressMap(progressRes.value));
    if (masteryRes.status === 'fulfilled') {
      setMastery(masteryRes.value);
      const chars = new Set<string>();
      for (const r of masteryRes.value) if (r.attempts > 0 && r.score < 40) chars.add(r.character);
      setWeak([...chars]);
    }
    if (challengeRes.status === 'fulfilled') setChallengeStats(challengeRes.value);
    if (rejected.length > 0) setError(true);
  }, [profile, nav]);

  useEffect(() => {
    if (!profile) return;
    void loadIndex().then(setIndex);
    void loadProgress();
  }, [profile, loadProgress]);

  if (!profile) return null;
  const lesson = index?.lessons.find((l) => l.id === profile.currentLessonId);
  const stars = today?.stars ?? 0;
  const goalReached = stars >= profile.dailyStarGoal;
  const pct = goalReached ? 100 : Math.round((stars / profile.dailyStarGoal) * 100);

  // Where the adventure path is up to, for the card's subtitle.
  const sections = index
    ? buildPath(index, profile.levelId, profile.higherChinese, adventureMode)
    : [];
  const statuses = index
    ? pathStatuses(sections, progress, unlockedOrder(index, profile.currentLessonId))
    : new Map();
  const nextId = currentNode(statuses);
  const nextStop = sections.flatMap((s) => s.nodes).find((n) => n.id === nextId) ?? null;
  const nextStopLesson = nextStop
    ? sections.find((s) => s.lesson.id === nextStop.lessonId)?.lesson
    : null;

  // A film that has unlocked and not been watched yet gets a nudge on the Cinema card.
  const newFilms = index
    ? buildFilms(index, profile.levelId).filter(
        (f) => filmUnlocked(f, sections, progress) && !filmWatched(f, progress),
      ).length
    : 0;

  const challengePool = index ? challengeCandidates(index, mastery, profile.higherChinese) : [];
  const todayStr = todayKey();
  // `streak.current` only changes on a goal-met day, so rendering it verbatim would keep showing
  // an old length through however many days have been missed since; `streakOn` zeroes it once the
  // goal hasn't been met since yesterday, the same rule `challengeStreakOn` applies below.
  const streakDays = streak ? streakOn(streak, todayStr) : 0;

  return (
    <Screen className="gap-5">
      <header className="flex items-center justify-between gap-3">
        <Link to="/" className="flex min-w-0 items-center gap-3">
          <Avatar id={normalizeAvatar(profile.avatar)} size={48} />
          <span className="truncate text-2xl font-semibold">{profile.nickname}</span>
        </Link>
        <div className="flex items-center gap-3">
          <span className="display hidden text-xl text-brand sm:inline">Ezi Shizi</span>
          <Link to="/parent" className="btn btn-ghost btn-sm" data-testid="parent-link">
            Parent
          </Link>
        </div>
      </header>
      {error && (
        <p className="-mt-3 text-bad" data-testid="home-error">
          Couldn&apos;t load progress ·{' '}
          <button type="button" className="underline" onClick={() => void loadProgress()}>
            Retry
          </button>
        </p>
      )}

      <section className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-3 rounded-3xl bg-white px-5 py-3 shadow-sm">
          <StarIcon className="h-8 w-8 text-accent" />
          <div className="flex flex-1 flex-col">
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold">{stars}</span>
              <span className="mb-1 text-muted">
                {goalReached ? 'goal done ✓' : `of ${profile.dailyStarGoal} today`}
              </span>
            </div>
            <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-black/5">
              <div
                className={`h-full rounded-full transition-all ${goalReached ? 'bg-ok' : 'bg-accent'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-3xl bg-white px-5 py-3 shadow-sm">
          <FlameIcon className="h-8 w-8 text-accent" />
          <span className="text-3xl font-bold">{streakDays}</span>
          <span className="text-muted">day streak</span>
        </div>
      </section>

      <section className="grid gap-4 landscape:grid-cols-2">
        {/* Adventure: the self-directed path with stories as rewards. */}
        <Link
          to="/path"
          search={{ mode: adventureMode }}
          className="pressable relative flex min-h-64 flex-col justify-between overflow-hidden rounded-[32px] p-6 text-white"
          style={{
            background:
              'linear-gradient(160deg, var(--color-brand) 0%, var(--color-brand-deep) 100%)',
          }}
          data-testid="go-path"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-white/70">
                Play
              </div>
              <div className="display text-4xl">Adventure</div>
            </div>
            <ModuleIcon id="path" className="h-10 w-10 text-white/80" />
          </div>
          <div className="absolute right-3 bottom-1 opacity-90 sm:right-4 sm:bottom-2">
            <span className="sm:hidden">
              <Shizi size={104} pose="idle" />
            </span>
            <span className="hidden sm:block">
              <Shizi size={150} pose="idle" />
            </span>
          </div>
          <div className="relative max-w-[62%] sm:max-w-none">
            {nextStop && nextStopLesson ? (
              <>
                <div className="text-lg text-white/80">
                  Lesson {nextStopLesson.order}
                  {nextStopLesson.title && (
                    <span className="hanzi ml-2">{nextStopLesson.title.zh}</span>
                  )}
                </div>
                <div className="text-xl font-bold sm:text-2xl">
                  Next:{' '}
                  {nextStop.kind === 'word' ? (
                    <span className="hanzi">{nextStop.char}</span>
                  ) : (
                    NODE_LABEL[nextStop.kind]
                  )}
                </div>
              </>
            ) : (
              <div className="text-2xl font-bold">
                {sections.length ? 'All done for now!' : 'Start the adventure'}
              </div>
            )}
            <span className="btn mt-3 bg-white text-brand">Play ▶</span>
          </div>
        </Link>

        {/* Study: focused practice per set, tests, review. */}
        <div className="flex min-h-64 flex-col justify-between rounded-[32px] bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted">
                Study
              </div>
              <div className="display text-4xl">
                {lesson ? `Lesson ${lesson.order}` : 'Lessons'}
              </div>
              {lesson?.title && (
                <div className="mt-1 text-xl text-muted">
                  <span className="hanzi">{lesson.title.zh}</span> · {lesson.title.en}
                </div>
              )}
            </div>
            <ModuleIcon id="read" className="h-10 w-10 text-brand" />
          </div>
          {lesson && (
            <div className="text-muted">
              {lesson.characterCount} characters · {lesson.setCount} sets · practice one set at a
              time, then take the test
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {lesson?.authored ? (
              <Link
                to="/lesson/$lessonId"
                params={{ lessonId: lesson.id }}
                className="btn btn-primary"
                data-testid="continue-lesson"
              >
                Open lesson {lesson.order} →
              </Link>
            ) : (
              <span className="text-bad">
                This lesson has no content yet. Ask a parent to pick another lesson.
              </span>
            )}
            <Link to="/study" className="btn btn-ghost btn-sm" data-testid="all-lessons">
              All lessons
            </Link>
            <Link to="/games" search={{}} className="btn btn-ghost btn-sm" data-testid="games-lab">
              Games lab
            </Link>
            {weak.length > 0 && lesson?.authored && (
              <Link
                to="/play/$lessonId/$module"
                params={{ lessonId: lesson.id, module: 'mistakes' }}
                search={{ from: 'home' }}
                className="btn btn-ghost btn-sm"
                onClick={() => setMistakeChars(weak)}
              >
                Fix mistakes ({weak.length})
              </Link>
            )}
          </div>
        </div>

        {/* Rewards to look forward to: the sticker book and the story films. */}
        <div className="grid grid-cols-2 gap-4 landscape:col-span-2">
          <Link
            to="/stickers"
            className="pressable flex items-center gap-4 rounded-[32px] bg-white p-5 shadow-sm"
            data-testid="go-stickers"
          >
            <StickerBookIcon className="h-10 w-10 shrink-0 text-accent" />
            <div>
              <div className="display text-2xl">Sticker book</div>
              <div className="text-muted">Finish islands to collect words</div>
            </div>
          </Link>
          <Link
            to="/films"
            className="pressable relative flex items-center gap-4 rounded-[32px] bg-white p-5 shadow-sm"
            data-testid="go-films"
          >
            <ClapperIcon className="h-10 w-10 shrink-0 text-brand" />
            <div>
              <div className="display text-2xl">Cinema</div>
              <div className="text-muted">Story films</div>
            </div>
            {newFilms > 0 && (
              <span
                className="absolute top-3 right-4 rounded-full bg-accent px-3 py-1 text-sm font-bold text-white"
                data-testid="films-new"
              >
                New!
              </span>
            )}
          </Link>
        </div>

        {/* Review: a randomised recall quiz over everything learned so far, biased to weak spots. */}
        <div
          className="relative flex min-h-40 flex-col justify-between gap-3 overflow-hidden rounded-[32px] bg-white p-6 pl-8 shadow-sm landscape:col-span-2"
          style={{ borderLeft: '8px solid var(--color-accent)' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted">
                Review
              </div>
              <div className="display text-4xl">Daily challenge</div>
            </div>
            <ModuleIcon id="challenge" className="h-10 w-10 text-accent" />
          </div>
          <div className="flex flex-wrap items-center gap-4" data-testid="challenge-stats">
            <span className="flex items-center gap-1 text-lg font-semibold">
              <FlameIcon className="h-5 w-5 text-accent" />
              {challengeStreakOn(challengeStats, todayStr)} day streak
            </span>
            <span className="text-muted">{challengesOn(challengeStats, todayStr)} today</span>
            <span className="text-muted">{challengeStats.total} total</span>
          </div>
          {challengePool.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/play/$lessonId/$module"
                params={{ lessonId: CHALLENGE_LESSON_ID, module: 'challenge' }}
                search={{ from: 'home' }}
                className="btn btn-primary"
                data-testid="go-challenge"
              >
                Challenge me ▶
              </Link>
              <span className="text-muted">{challengePool.length} words in the mix</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-muted">Learn a few words on the adventure first</span>
              <Link to="/path" className="btn btn-ghost btn-sm" data-testid="go-challenge">
                Start the adventure →
              </Link>
            </div>
          )}
        </div>
      </section>
    </Screen>
  );
}
